import { memo, useContext, useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';
import { usePageSpaceKey } from '@confluence/page-context';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import type { companyHubItemCallbackArgs } from '@confluence/company-hub-utils/entry-points/companyHubItemCallback';
import { useCompanyHubName } from '@confluence/company-hub-utils/entry-points/useCompanyHubName';
import {
	ADMIN_COMPANY_HUB_PERMISSIONS,
	COMPANY_HUB,
	COMPANY_HUB_ANALYTICS,
	COMPANY_HUB_SETTINGS,
} from '@confluence/named-routes/entry-points/routes';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';

const lazyOnClick = async (args: companyHubItemCallbackArgs) => {
	const { companyHubItemCallback } = await import(
		/* webpackChunkName: "loadable-companyHubItemCallback" */ '@confluence/company-hub-utils/entry-points/companyHubItemCallback'
	);
	await companyHubItemCallback(args);
};

// While app-navigation in nav v3 and side-navigation in nav v4 both have CompanyHubItem, both are still maintained, and they're more common and different, AbstractCompanyHubItem contains the common parts.
export const AbstractCompanyHubItem = withFlags(
	memo(({ children, flags, navVersion }: AbstractCompanyHubItemProps & WithFlagsProps) => {
		const companyHubName = useCompanyHubName();
		const { match, push } = useContext(RoutesContext);
		const [spaceKey] = usePageSpaceKey();

		const routeName = match?.name;

		const { createAnalyticsEvent } = useAnalyticsEvents();
		const experienceTracker = useContext(ExperienceTrackerContext);
		const onClick = useCallback(async () => {
			void lazyOnClick({
				createAnalyticsEvent,
				experienceTracker,
				flags,
				isFirstTimeVisit: false,
				push,
				routeName,
				navVersion,
			});
		}, [createAnalyticsEvent, experienceTracker, flags, navVersion, push, routeName]);

		const isSelected =
			routeName === COMPANY_HUB.name ||
			(routeName !== ADMIN_COMPANY_HUB_PERMISSIONS.name &&
				routeName !== COMPANY_HUB_ANALYTICS.name &&
				routeName !== COMPANY_HUB_SETTINGS.name &&
				isCompanyHubSpaceKey(spaceKey));

		return children({ companyHubName, isSelected, namedRoute: COMPANY_HUB, onClick });
	}),
);

type AbstractCompanyHubItemProps = {
	navVersion: '3' | '4';
	children: (props: {
		companyHubName: string;
		isSelected: boolean;
		namedRoute: typeof COMPANY_HUB;
		onClick(): void;
	}) => JSX.Element;
};
